import { auth } from './firebase'
import { flushTodo } from './flush'
import { idbGet, STORE_CACHE, STORE_DRAFTS } from './idb'

// Wrapper único de la API: adjunta Bearer, parsea el contrato {error:{code,message}} y
// convierte el 401 en logout (la sesión expiró o fue revocada).
export class ApiFetchError extends Error {
  constructor(
    public code: string,
    public status: number,
    message: string,
    public extra?: Record<string, unknown>,
  ) {
    super(message)
  }
}

// Los dos 401 se cuentan como lo que son y dicen qué hacer. El `message` de un
// ApiFetchError se pinta TAL CUAL en las bandas de error (PostEditor, RolesPanel,
// PermisosModal, DesdeTexto…), así que "Sin sesión" era literalmente todo lo que
// leía la persona: ni la causa, ni el siguiente paso, ni que su texto está a salvo
// —justo lo que sí dicen 'sin_red' y 'red_lenta'—. El aviso de que nada se perdió
// no es un consuelo de cortesía: lo respalda el volcado a disco de abajo.
const MSG_EXPIRADA =
  'Tu sesión expiró. Lo que estabas escribiendo quedó guardado en este equipo: vuelve a entrar y sigue donde ibas.'
const MSG_VERSION_VIEJA =
  'camp se actualizó mientras tenías esto abierto. Recarga la página para seguir.'

const MSG_SIN_SESION =
  'Tu sesión no está activa en este equipo. Lo que estabas escribiendo quedó guardado aquí: vuelve a entrar y sigue donde ibas.'

// La expiración pasa UNA sola vez por vida de la página: una pantalla con varias
// queries en vuelo daba un signOut y una navegación por cada 401.
let cerrando: Promise<void> | null = null

// Techo para la espera de disco, mismo argumento que persist.ts: idb degrada a no-op
// si FALLA, pero puede COLGARSE (otra pestaña con la base abierta en otra versión).
// Antes que dejar a alguien mirando una pantalla muerta, se navega sin confirmación.
const TOPE_DISCO_MS = 1_500

// Llave que no existe en ninguno de los dos stores: solo se usa como barrera.
const LLAVE_BARRERA = '__barrera__'

/**
 * Espera a que IndexedDB CONFIRME lo que se acaba de volcar. No es una pausa a ojo:
 * el navegador no corre a la vez dos transacciones cuyo alcance se pisa si alguna
 * escribe, y las arranca en el orden en que se crearon. Por eso una lectura pedida
 * DESPUÉS de los volcados no puede empezar antes de que esas escrituras hayan
 * cerrado, y leer una llave inexistente es la forma más barata de que el disco avise
 * de que ya terminó.
 */
function esperarDisco(): Promise<void> {
  const barrera = Promise.all([
    idbGet(STORE_DRAFTS, LLAVE_BARRERA),
    idbGet(STORE_CACHE, LLAVE_BARRERA),
  ])
  return new Promise((resolve) => {
    const t = setTimeout(resolve, TOPE_DISCO_MS)
    const fin = () => {
      clearTimeout(t)
      resolve()
    }
    void barrera.then(fin, fin)
  })
}

/**
 * Cierre por sesión expirada. El orden es el arreglo:
 *
 *  1. VOLCAR PRIMERO, con la app todavía viva. El borrador se guarda solo con 800 ms
 *     de debounce (drafts.ts), así que al expirar hay hasta 800 ms de tecleo que aún
 *     no tocó el disco. Confiar en el volcado de 'pagehide' no alcanzaba: una
 *     escritura arrancada cuando la navegación ya empezó no tiene garantía de cerrar.
 *  2. ESPERAR al disco antes de tocar nada más.
 *  3. signOut y recién entonces navegar: signOut dispara el listener de auth, que
 *     desmonta el editor; si se hiciera antes del volcado, el estado a guardar ya no
 *     existiría.
 */
async function cerrarSesionExpirada(): Promise<void> {
  flushTodo()
  await esperarDisco()
  try {
    const { signOut } = await import('firebase/auth')
    await signOut(auth)
  } catch {
    // Ni el módulo que no carga (sin red, chunk caído) puede dejar a alguien atrapado
    // en una pantalla que ya no puede pedir nada: se navega igual y /bienvenida
    // rehace el estado de sesión desde cero.
  }
  // El destino conserva la búsqueda además de la ruta: se vuelve a la MISMA pantalla
  // (un filtro del tablero, un mensaje abierto), no a su versión en blanco.
  const destino = `${window.location.pathname}${window.location.search}`
  window.location.assign(`/bienvenida?expired=1&next=${encodeURIComponent(destino)}`)
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit & { ifMatch?: string } = {},
): Promise<T> {
  // Esperar a que Auth termine de restaurar la sesión persistida. Sin esto, la
  // primera petición tras una recarga podía salir SIN token: en la app de
  // escritorio Firebase valida la sesión con un viaje al servidor antes de
  // exponer currentUser, y mientras tanto el caché hidratado de react-query ya
  // había pintado la app y disparado sus queries. El 401 resultante cerraba la
  // sesión y mandaba a /bienvenida, que al cargar volvía a hacer lo mismo: bucle.
  await auth.authStateReady()
  const user = auth.currentUser
  const token = user ? await user.getIdToken() : null
  const headers = new Headers(init.headers)
  if (token) headers.set('authorization', `Bearer ${token}`)
  if (init.body && !headers.has('content-type')) headers.set('content-type', 'application/json')
  if (init.ifMatch) headers.set('if-match', init.ifMatch)

  // Un fallo de red lanza TypeError('Failed to fetch'), NO un ApiFetchError, así que
  // las cinco pantallas que hacen `err instanceof ApiFetchError` caían al mensaje
  // genérico y el usuario leía "Failed to fetch". Se traduce a un código propio.
  // El timeout existe porque sin él, en red 'lie-fi' (conectado pero sin salida) la
  // promesa cuelga para siempre y la pantalla se queda en skeleton.
  let res: Response
  try {
    res = await fetch(path, { ...init, headers, signal: init.signal ?? AbortSignal.timeout(15_000) })
  } catch (err) {
    const abortada = err instanceof DOMException && err.name === 'TimeoutError'
    throw new ApiFetchError(
      abortada ? 'red_lenta' : 'sin_red',
      0,
      abortada ? 'La conexión tardó demasiado' : 'Sin conexión',
    )
  }
  // 410: el servidor retiró esta ruta porque quien la llama trae un bundle viejo.
  // Va ANTES del 401 a propósito de lectura, no de control: los dos status son
  // distintos, pero quien lea este archivo tiene que ver juntos los dos casos de
  // "tu app no está donde debería" y notar que uno cierra sesión y el otro NO.
  // Cerrar sesión aquí sería el peor desenlace posible: la persona pierde el lugar
  // donde estaba por un problema que se arregla recargando.
  if (res.status === 410) {
    let code = 'version_vieja_app'
    try {
      const body = (await res.json()) as { error?: { code?: string } }
      code = body.error?.code ?? code
    } catch {
      // Un 410 sin cuerpo JSON igual significa que esta ruta ya no existe.
    }
    if (code === 'version_vieja_app') {
      // Dinámico, como el signOut de abajo: swUpdate registra el service worker al
      // cargarse, y eso no tiene por qué entrar al chunk de arranque por una rama
      // que casi nunca corre.
      const { marcarVersionVieja } = await import('./swUpdate')
      marcarVersionVieja()
      throw new ApiFetchError(code, 410, MSG_VERSION_VIEJA)
    }
    throw new ApiFetchError(code, 410, `Error ${res.status}`)
  }
  if (res.status === 401) {
    // Sin token no hubo sesión que expirara: es que la petición salió antes de
    // tiempo (o sin usuario). Cerrar sesión y redirigir aquí solo alimentaría el
    // bucle de arriba; se devuelve el error y quien llamó decide.
    if (!token) throw new ApiFetchError('no_autenticado', 401, MSG_SIN_SESION)
    // El cierre va por su cuenta y el error se lanza YA. El assign iba primero y se
    // llevaba la pantalla sin pasar por el onError de quien llamó: la persona veía
    // irse el editor y nunca leía un aviso. Ahora el error llega a tiempo de pintar
    // la banda, y la navegación ocurre después —cuando el borrador ya está en disco.
    cerrando ??= cerrarSesionExpirada()
    throw new ApiFetchError('no_autenticado', 401, MSG_EXPIRADA)
  }
  if (!res.ok) {
    let code = 'error'
    let message = `Error ${res.status}`
    let extra: Record<string, unknown> | undefined
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } & Record<string, unknown> }
      if (body.error) {
        const { code: c, message: m, ...rest } = body.error
        code = c ?? code
        message = m ?? message
        extra = rest
      }
    } catch {
      /* respuesta sin JSON */
    }
    throw new ApiFetchError(code, res.status, message, extra)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

export const apiGet = <T>(path: string) => apiFetch<T>(path)
export const apiPost = <T>(path: string, body?: unknown) =>
  apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })
export const apiPut = <T>(path: string, body: unknown, ifMatch?: string) =>
  apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body), ifMatch })
export const apiPatch = <T>(path: string, body: unknown, ifMatch?: string) =>
  apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body), ifMatch })
export const apiDelete = <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' })
